import { Box, Stack, Typography } from '@mui/material'
import { ClientCreateFloorPlanRequestDrawer, ClientGalleryCreateRequestDrawer, FloorPlanRequestSource } from 'components/pages/Client/ClientGallery/ClientGalleryCreateRequestDrawer'
import { Dispatch, FC, SetStateAction, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { GalleryTab, useGalleryAssignment, useGalleryProduct } from '../../contexts'
import { useAcceptDocument, useGetAssignmentDocuments } from 'dataQueries/assignmentDocuments.query'
import { useAuth0 } from 'utils/auth'
import { useLocation, useNavigate } from 'react-router'

import { AssignmentDocumentDTO } from 'models/assignment'
import CampaignIcon from '@mui/icons-material/Campaign'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import { ClientAssignmentReference } from 'components/pages/Property/ClientProperty/ClientAssignmentReference'
import { ClientGalleryDocumentFiles } from '../ClientGalleryDocumentFiles'
import { ClientGalleryDocumentTimeline } from '../ClientGalleryDocumentTimeline'
import { ClientGalleryRequestThreadList } from '../ClientGalleryRequestThreadList'
import { DocumentUploadType } from 'constants/documents'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import { MUIButton } from 'components/common/MUIButton'
import { Nullable } from 'models/helpers'
import { RealEstatePropertyDTO } from 'models/property'
import { RequestThread } from 'components/common/Threads'
import { useClientGallery } from '../_main'
import { useThreads } from 'components/common/Threads/context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

enum DocumentContentMode {
  DOCUMENTS = 'DOCUMENTS',
  REQUESTS = 'REQUESTS'
}

interface CreateRequestButtonProps {
  disabled: boolean
  onCreateRequest: () => void
  t: (key: string) => string
}

/**
 * @interface Props
 */
interface Props {
  /** Current selected assignmentId */
  assignmentId: string
  /** Callback to set active tab - is used for old Gallery flow only */
  setActiveTab?: Dispatch<SetStateAction<GalleryTab>>
  /** Property data for analytics - only available when rendered in Property page context */
  propertyData?: RealEstatePropertyDTO
}

/** `CreateRequestButton` is a memoized functional component that renders a button for sending feedback. */
const CreateRequestButton = memo<CreateRequestButtonProps>(({ disabled, onCreateRequest, t }) => (
  <MUIButton
    size="sm"
    type="secondaryNoBorder"
    startIcon={<CampaignIcon />}
    disabled={disabled}
    onClick={() => !disabled && onCreateRequest()}
  >
    {t('send_feedback')}
  </MUIButton>
))

/**
 * Client gallery documents component
 * 
 * @example
 * <ClientGalleryDocuments />
 */
export const ClientGalleryDocuments: FC<Props> = ({ assignmentId, setActiveTab, propertyData }) => {
  const { t } = useTranslation(['gallery'])
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const threadId = searchParams.get('threadId')

  const { userPropertyWorkspaceId } = useUserData()
  const { roles } = useAuth0()

  const {
    isEnergyCertificateProduct,
    isFloorPlanCertificationProduct,
  } = useGalleryProduct()

  const {
    isDocumentWithValidateDraft,
    allowClientToCreateThreads
  } = useGalleryAssignment()

  const {
    assignmentDocumentsStatusHistoryData,
    assignmentDocumentsEstimatedDelivery,
    showDocumentStatusHistory
  } = useClientGallery()

  const {
    threadList,
    fileAPIOptions,
    handleOnCreateRequestThread,
    handleCloseCreateRequestDrawer,
    selectedThread,
    setSelectedThread,
    isCreateRequestDrawerOpen,
    setIsCreateRequestDrawerOpen
  } = useThreads()

  const getAssignmentOutputDocuments = useGetAssignmentDocuments(assignmentId, DocumentUploadType.OUTPUT, userPropertyWorkspaceId, roles)
  const assignmentDocumentsData = useMemo(() => getAssignmentOutputDocuments.data?.data || [], [getAssignmentOutputDocuments.data])
  const acceptDocument = useAcceptDocument()

  const [selectedDoc, setSelectedDoc] = useState<Nullable<AssignmentDocumentDTO>>()
  const [isCreateFloorPlanRequestDrawerOpen, setIsCreateFloorPlanRequestDrawerOpen] = useState(false)
  const [selectedMode, setSelectedMode] = useState<DocumentContentMode>(DocumentContentMode.DOCUMENTS)

  const handleThreadIdInUrl = useCallback((threadId?: string) => {
    const searchParams = new URLSearchParams(location.search)
    threadId ? searchParams.set('threadId', threadId) : searchParams.delete('threadId')
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
  }, [location.pathname, location.search, navigate])

  useLayoutEffect(() => {
    if (threadId) {
      setSelectedThread(threadList.find(thread => thread.id === threadId) || null)
      setSelectedMode(DocumentContentMode.REQUESTS)
    }

    // Show the thread content if threadId is present in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Cleared on an empty listing too - a selection kept from the previously viewed assignment would send that
    // assignment's correction reference against this one.
    setSelectedDoc(assignmentDocumentsData[0] ?? null)
  }, [assignmentDocumentsData])

  const handleCreateRequest = useCallback(() => {
    if (isFloorPlanCertificationProduct) {
      setIsCreateFloorPlanRequestDrawerOpen(true)
      return
    }

    if (isEnergyCertificateProduct && typeof setActiveTab === 'function') {
      setActiveTab(GalleryTab.REQUESTS)
      return
    } else if (isEnergyCertificateProduct && threadList.length > 0) {
      setSelectedThread(threadList[0])
      setSelectedMode(DocumentContentMode.REQUESTS)
      return
    }

    setIsCreateRequestDrawerOpen(true)
  }, [isEnergyCertificateProduct, isFloorPlanCertificationProduct, setActiveTab, setIsCreateRequestDrawerOpen, setSelectedThread, threadList])

  return (
    <DynamicQueryContent query={getAssignmentOutputDocuments}>

      {allowClientToCreateThreads && !isDocumentWithValidateDraft &&
        <Stack width="100%" gap="0.8rem" marginTop="3.2rem" flexDirection="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">

          <ClientAssignmentReference />

          {selectedMode === DocumentContentMode.DOCUMENTS &&
            <CreateRequestButton
              t={t}
              disabled={!allowClientToCreateThreads}
              onCreateRequest={() => handleCreateRequest()}
            />
          }

        </Stack>
      }

      {!allowClientToCreateThreads && !isDocumentWithValidateDraft &&
        <Stack width="100%" gap="0.8rem" marginTop="3.2rem" flexDirection="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <ClientAssignmentReference />
        </Stack>
      }

      {isDocumentWithValidateDraft &&
        <Stack width="100%" marginTop="3.2rem" gap="0.8rem" justifyContent="space-between">
          <ClientAssignmentReference />

          <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>{t('client_gallery.review_draft')}</Typography>

          <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="flex-end">

            <Box maxWidth="56rem">
              <Typography variant="text-sm" fontWeight={400} color={GRAY_700}>{t('client_gallery.review_draft_note')}</Typography>
            </Box>

            <Stack gap="0.8rem" flexDirection="row">
              {selectedMode === DocumentContentMode.DOCUMENTS &&
                <CreateRequestButton
                  t={t}
                  disabled={!allowClientToCreateThreads}
                  onCreateRequest={() => handleCreateRequest()}
                />
              }

              <MUIButton
                type="defaultPrimary"
                size="sm"
                startIcon={<CheckCircleOutlineRoundedIcon />}
                isLoading={acceptDocument.isPending}
                onClick={() => acceptDocument.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId })}
              >
                {t('client_gallery.validate_document')}
              </MUIButton>
            </Stack>

          </Stack>

        </Stack>
      }

      <Stack width="100%" marginTop="3.2rem" gap={4} display="flex" flexDirection="row" justifyContent="space-between">

        {(assignmentDocumentsData.length > 0 || showDocumentStatusHistory || threadList.length > 0) &&
          <Stack gap="2rem" height="100%" flex="0 1 34rem" minWidth={0} position="sticky">

            {assignmentDocumentsData.length > 0 &&
              <ClientGalleryDocumentFiles
                assignmentId={assignmentId}
                assignmentDocumentsData={assignmentDocumentsData}
                selectedDoc={selectedDoc}
                propertyData={propertyData}
                setSelectedDoc={(docData) => {
                  setSelectedDoc(docData)
                  setSelectedMode(DocumentContentMode.DOCUMENTS)
                  handleThreadIdInUrl()
                }}
              />
            }

            {threadList.length > 0 &&
              <ClientGalleryRequestThreadList
                threadMessageItems={threadList}
                selectedThread={selectedThread}
                setSelectedThread={(threadData) => {
                  setSelectedThread(threadData)
                  setSelectedMode(DocumentContentMode.REQUESTS)
                  threadData?.id && handleThreadIdInUrl(threadData.id)
                }}
              />
            }

            {showDocumentStatusHistory &&
              <ClientGalleryDocumentTimeline
                documentStatusHistoryData={assignmentDocumentsStatusHistoryData}
                assignmentDocumentsEstimatedDelivery={assignmentDocumentsEstimatedDelivery}
              />
            }

          </Stack>
        }

        {assignmentDocumentsData.length > 0 && selectedMode === DocumentContentMode.DOCUMENTS &&
          <Stack flex="1 1 75%">
            <iframe
              src={selectedDoc?.signedUrl.signedURL}
              width="100%"
              height="1200px"
              style={{ border: 'none' }}
              title={t('pdf_preview')}
            />
          </Stack>
        }

        {selectedThread && selectedMode === DocumentContentMode.REQUESTS &&
          <Stack flex="1 1 75%">
            <RequestThread
              assignmentId={assignmentId}
              title={t('threads:request_supplier_title')}
              threadMessageItems={[selectedThread]}
              fileUploadOptions={fileAPIOptions}
              onHeaderButtonClick={handleOnCreateRequestThread}
            />
          </Stack>
        }

      </Stack>

      {!isFloorPlanCertificationProduct &&
        <ClientGalleryCreateRequestDrawer
          isOpen={isCreateRequestDrawerOpen}
          onClose={handleCloseCreateRequestDrawer}
          allowUploadOfDocuments
        />
      }

      {isFloorPlanCertificationProduct &&
        <ClientCreateFloorPlanRequestDrawer
          isOpen={isCreateFloorPlanRequestDrawerOpen}
          onClose={() => setIsCreateFloorPlanRequestDrawerOpen(false)}
          source={FloorPlanRequestSource.CLIENT_GALLERY}
          externalReference={selectedDoc?.signedUrl.filename ?? ''}
          correctionReference={selectedDoc?.correctionReference}
        />
      }

    </DynamicQueryContent>
  )
}
